/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { Log } from '../logging/Log';

export class Storage {
    private TAG: string = 'Storage';

    public static StorageType = {
        LOCAL_STORAGE: localStorage,
        SESSION_STORAGE: sessionStorage,
    };

    public static Keys = {
        APP_CRASH_RETRY_LIMIT: 'APP_CRASH_RETRY_LIMIT',
        APP_STATE: 'APP_STATE',
        AUTH_CONFIGURATIONS: 'AUTH_CONFIGURATIONS',
        BODY_INFO_SERVICE_DATA: 'BODY_INFO_SERVICE_DATA',
        BODY_INFO_SERVICE_LAST_CALL_TIME: 'BODY_INFO_SERVICE_LAST_CALL_TIME',
        CA_DEVICE_ID: 'CA_DEVICE_ID',
        CCB_OVERRIDDEN_SWITCHES: 'CCB_OVERRIDDEN_SWITCHES',
        CCB_SWITCH_PARTNER: 'CCB_SWITCH_PARTNER',
        CCB_SWITCH_VERSION: 'CCB_SWITCH_VERSION',
        CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME: 'CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME',
        CHANNELS_V2_DATA_LOCAL_CACHE_EXPIRY_TIME: 'CHANNELS_V2_DATA_LOCAL_CACHE_EXPIRY_TIME',
        CHANNELS_SNAPSHOT_VERSION: 'CHANNELS_SNAPSHOT_VERSION',
        CHANNELS_V2_SNAPSHOT_VERSION: 'CHANNELS_V2_SNAPSHOT_VERSION',
        CHANNELS_TYPE: 'CHANNELS_TYPE',
        CHANNELS: 'CHANNELS',
        CHANNELS_V2: 'CHANNELS_V2',
        CUBIWARE_AUTH_DONE: 'CUBIWARE_AUTH_DONE',
        DEVICE_UUID: 'DEVICE_UUID',
        FILTERED_OUT_CHANNELS: 'FILTERED_OUT_CHANNELS',
        FILTERED_OUT_CHANNELS_V2: 'FILTERED_OUT_CHANNELS_V2',
        ENABLE_NETWORK_LOGS: 'ENABLE_NETWORK_LOGS',
        IN_HOME_LOCATION_CONSUMED_GRACE_PERIOD: 'IN_HOME_LOCATION_CONSUMED_GRACE_PERIOD',
        IN_HOME_LOCATION_DATA: 'IN_HOME_LOCATION_DATA',
        INTERNAL_RATINGS_SERVICE_DATA: 'INTERNAL_RATINGS_SERVICE_DATA',
        INTERNAL_RATINGS_SERVICE_LAST_CALL_TIME: 'INTERNAL_RATINGS_SERVICE_LAST_CALL_TIME',
        IS_APP_ALREADY_LAUNCHED: 'IS_APP_ALREADY_LAUNCHED',
        IS_AUTOPLAY_NEXT_EPISODE_ENABLED: 'IS_AUTOPLAY_NEXT_EPISODE_ENABLED',
        IS_VIDEO_DEBUG_OVERLAY_ACTIVE: 'IS_VIDEO_DEBUG_OVERLAY_ACTIVE',
        IS_VIDEO_WINDOW_ENABLED: 'IS_VIDEO_WINDOW_ENABLED',
        LANGUAGE: 'LANGUAGE',
        LAST_TUNED_CHANNEL: 'LAST_TUNED_CHANNEL',
        LICENSE_PLATE_AUTH_CODE: 'LICENSE_PLATE_AUTH_CODE',
        LOCALE: 'LOCALE',
        LOGGING_WINDOW: 'LOGGING_WINDOW',
        MSO_SERVICE_ID: 'MSO_SERVICE_ID',
        PC_LOCK_TIMER: 'PC_LOCK_TIMER',
        PC_SETTING: 'PC_SETTING',
        PLAYER_AUDIO_LANGUAGE: 'PLAYER_AUDIO_LANGUAGE',
        PLAYER_CC_LANGUAGE: 'PLAYER_CC_LANGUAGE',
        PLAYER_CC_LANGUAGE_KIND: 'PLAYER_CC_LANGUAGE_KIND',
        PLAYER_CC_STATUS: 'PLAYER_CC_STATUS',
        QUICK_MENU_SHORTCUT: 'QUICK_MENU_SHORTCUT',
        SAML_TOKEN_WITH_EXPIRY: 'SAML_TOKEN_WITH_EXPIRY',
        SCOPE_DOMAIN_TOKENS: 'SCOPE_DOMAIN_TOKENS',
        SLS_SERVICE_ENDPOINTS: 'SLS_SERVICE_ENDPOINTS',
        STATION_POLICIES: 'STATION_POLICIES',
        STATION_POLICY_SERVICE_LAST_CALL_TIME: 'STATION_POLICY_SERVICE_LAST_CALL_TIME',
        STATION_POLICY_SNAPSHOT_VERSION: 'STATION_POLICY_SNAPSHOT_VERSION',
        VIDEO_PROVIDER_CONFIGURATIONS_DATA: 'VIDEO_PROVIDER_CONFIGURATIONS_DATA',
        VIDEO_PROVIDER_CONFIGURATIONS_LAST_CALL_TIME:
            'VIDEO_PROVIDER_CONFIGURATIONS_LAST_CALL_TIME',
    };

    private static localStorage;

    private static sessionStorage;

    private storageType;

    private constructor(storageType) {
        this.storageType = storageType;
    }

    public static getInstance(storage = Storage.StorageType.LOCAL_STORAGE) {
        if (storage === Storage.StorageType.SESSION_STORAGE)
            return Storage.getInstanceSessionStorage();
        return Storage.getInstanceLocalStorage();
    }

    public static getInstanceLocalStorage() {
        if (!Storage.localStorage)
            Storage.localStorage = new Storage(Storage.StorageType.LOCAL_STORAGE);
        return Storage.localStorage;
    }

    public static getInstanceSessionStorage() {
        if (!Storage.sessionStorage)
            Storage.sessionStorage = new Storage(Storage.StorageType.SESSION_STORAGE);
        return Storage.sessionStorage;
    }

    public static clearAll(preserveData?: string[]) {
        const preservedStates: { [key: string]: any } = {};
        preserveData?.forEach((key) => {
            preservedStates[key] = Storage.getInstance().getItem(key);
        });
        Storage.sessionStorage?.clear();
        Storage.localStorage?.clear();
        preserveData?.forEach((key) => Storage.getInstance().setItem(key, preservedStates[key]));
    }

    public clearData() {
        const dataKeys = [
            Storage.Keys.CHANNELS,
            Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
            Storage.Keys.CHANNELS_SNAPSHOT_VERSION,
            Storage.Keys.IN_HOME_LOCATION_DATA,
            Storage.Keys.INTERNAL_RATINGS_SERVICE_DATA,
            Storage.Keys.STATION_POLICIES,
            Storage.Keys.STATION_POLICY_SNAPSHOT_VERSION,
            Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA,
        ];

        dataKeys.forEach((key) => {
            this.removeItem(key);
        });

        Storage.getInstance(Storage.StorageType.SESSION_STORAGE).clearSessionData();
    }

    public clearSessionData() {
        const dataKeys = [Storage.Keys.BODY_INFO_SERVICE_DATA];

        dataKeys.forEach((key) => {
            this.removeItem(key);
        });
    }

    public setItem(key: string, value: any): void {
        if (!this.storageType) {
            Log.e(this.TAG, 'Storage not initialized, create storage instance first');
            throw new Error('Storage not initialized, create storage instance first');
        }
        const data = {};
        data[key] = value;
        this.storageType.setItem(key, JSON.stringify(data));
    }

    public removeItem(key: string): void {
        this.storageType.removeItem(key);
    }

    public getItem(key: string): any {
        if (!this.storageType) {
            Log.e(this.TAG, 'Storage not initialized, create storage instance first');
            throw new Error('Storage not initialized, create storage instance first');
        }
        const data = this.storageType.getItem(key);
        if (!data) return null;
        const jsonData = JSON.parse(data);
        return jsonData[key];
    }

    public getItemWithDefaultValue(key: string, defaultValue: any): any {
        const data = this.getItem(key);
        if (!data) return defaultValue;
        return data;
    }

    public clear(): void {
        if (this.storageType) this.storageType.clear();
    }
}
